/**
 * Cargo commands (plan §6 M2-05): ore, components and relay parts (M3 D-TBD 40) into and out of the hold,
 * data-core logging, and a base-price estimate of the hold for the encounter director (goods included, SY-2b).
 * Pure and immutable; refusals are typed results.
 *
 * **M7 S14 (spec §2.1, §2.2):** picking up a generic data core also copies a log into the story ledger, unread
 * (`story/hooks.ts copyCoreLog`); the first core at the Harrow POI carries the fixed `evacuation` log. The caller
 * passes where the core came from (`CoreOrigin`); selling the core later never touches the ledger.
 *
 * Counting: `addOre` is the "ore collected" command, so it also counts `stats.oreT` (and `stats.tetherstoneT`).
 * `addComponent` does not count `stats.partsSecured`: it also stores contract pods, so the career runtime counts
 * secured parts from the `partSecured` event.
 */
import { logger } from '../core/logger'
import { WARE_IDS, type M2Data, type WareId } from '../data/m2Schemas'
import { NO_ORIGIN, copyCoreLog, isGenericCore, type CoreOrigin } from '../story/hooks'
import { cargoClassOf, holdMultOf, holdUsedT, rawHoldMultOf } from './hullFit'
import { EPSILON, cargoFreeT, waresT, withComponent, withGood, withoutComponent, withWare } from './ops'
import { GOOD_IDS, GOOD_MASS_T, goodT, goodsBaseValue, goodsT, isRelayPartId, type RelayPartId } from './parts'
import { cargoFull, fail, invalidAmount, ok, type CareerEffect, type CmdResult } from './result'
import type { CargoComponent, CargoContents, Career } from './types'

/**
 * Adds `tonnes` of `ware` to the hold. Refuses past capacity with `cargoFull` (the in-space collector already
 * checked through the mirror, so a refusal here means the mirror was stale and is logged). M10 P5a6a: the ore takes
 * `tonnes ÷ holdMult[cargoClass(ware)]` of the hold (raw ore 1× on a processor hull until processed, Q4).
 */
export function addOre(career: Career, ware: WareId, tonnes: number): CmdResult<Career> {
  if (!Number.isFinite(tonnes) || tonnes <= 0) return invalidAmount('ore tonnage', tonnes)
  if (!WARE_IDS.includes(ware)) return fail('invalidAmount', `Unknown ware: ${String(ware)}.`, { ware: String(ware) })
  const free = cargoFreeT(career)
  const need = tonnes / rawHoldMultOf(career.ship.defId, cargoClassOf(ware))
  if (need > free + EPSILON) {
    logger.warn('career: ore refused, hold full (stale cargo mirror)', { ware, tonnes, free })
    return cargoFull(need, free)
  }
  const next = withWare(career, ware, tonnes)
  const stats = {
    ...next.stats,
    oreT: next.stats.oreT + tonnes,
    tetherstoneT: ware === 'tetherstone' ? next.stats.tetherstoneT + tonnes : next.stats.tetherstoneT,
  }
  return ok({ ...next, stats })
}

/** Whole units of relay `part` that fit in the free hold now. */
export function partsThatFit(career: Career, part: RelayPartId): number {
  return Math.floor((cargoFreeT(career) + EPSILON) / partHoldT(career, part, 1))
}

/**
 * Adds `qty` whole units of relay `part` to the hold (D-TBD 40). All or nothing: refuses past capacity with
 * `cargoFull` (use `partsThatFit` to clamp first) and a bad count or unknown part with `invalidAmount`. The
 * whole-unit guard lives here (MC-b1): the relay moves whole units on the float goods primitive `withGood`.
 */
export function addParts(career: Career, part: RelayPartId, qty: number): CmdResult<Career> {
  if (!Number.isInteger(qty) || qty <= 0) return invalidAmount('part count', qty)
  if (!isRelayPartId(part)) return fail('invalidAmount', `Unknown part: ${String(part)}.`, { part: String(part) })
  const needed = partHoldT(career, part, qty)
  const free = cargoFreeT(career)
  if (needed > free + EPSILON) return cargoFull(needed, free)
  return ok(withGood(career, part, qty))
}

/** Hold tonnes `qty` units of relay `part` take on the flown hull (M10 P5a6a: ÷ its class's `holdMult`; 1× today). */
function partHoldT(career: Career, part: RelayPartId, qty: number): number {
  return (qty * GOOD_MASS_T[part]) / holdMultOf(career.ship.defId, cargoClassOf(part))
}

/** Removes `qty` whole units of relay `part` from the hold. Refuses with `notInCargo` when fewer are carried. */
export function removeParts(career: Career, part: RelayPartId, qty: number): CmdResult<Career> {
  if (!Number.isInteger(qty) || qty <= 0) return invalidAmount('part count', qty)
  if (!isRelayPartId(part)) return fail('invalidAmount', `Unknown part: ${String(part)}.`, { part: String(part) })
  const have = goodT(career.cargo.parts, part)
  if (have < qty) return fail('notInCargo', `Only ${have} of that part in the hold, ${qty} needed.`, { part, have, needed: qty })
  return ok(withGood(career, part, -qty))
}

/** Tonnes a pod's contents take once unpacked (wares, goods (SY-2b) and each component's carried mass). */
export function contentsMassT(contents: CargoContents): number {
  let t = waresT(contents.wares) + goodsT(contents.goods)
  for (const c of contents.components) t += c.massT
  return t
}

/**
 * Logs `c` into `career.dataCores` when it is a data core not logged yet (the effect goes into `effects`); a
 * generic core also copies its story log, unread (M7 S14). A core already logged (a death pod picked up again)
 * copies nothing new.
 */
function logDataCore(career: Career, c: CargoComponent, effects: CareerEffect[], origin: CoreOrigin): Career {
  if (c.type !== 'dataCore' || career.dataCores.includes(c.id)) return career
  effects.push({ kind: 'dataCoreLogged', id: c.id })
  const logged: Career = { ...career, dataCores: [...career.dataCores, c.id] }
  return isGenericCore(c) ? copyCoreLog(logged, c.id, origin) : logged
}

/**
 * Adds a component to the hold. A data core is logged into `dataCores` once (`dataCoreLogged`). A pod with
 * `contents` (a death-wreck pod) is unpacked into the hold when the contents fit, else kept as a pod when the
 * pod fits; sealed contract pods are never unpacked. Refuses with `cargoFull` when nothing fits.
 * `origin` (M7 S14): where the component was picked up (the Harrow test reads its POI); cores unpacked from a pod
 * have no site.
 */
export function addComponent(career: Career, c: CargoComponent, origin: CoreOrigin = NO_ORIGIN): CmdResult<Career> {
  if (!Number.isFinite(c.massT) || c.massT < 0) return invalidAmount('component mass', c.massT)
  if (career.cargo.components.some((x) => x.id === c.id)) {
    return fail('invalidAmount', `Component ${c.id} is already in the hold.`, { id: c.id })
  }
  const free = cargoFreeT(career)
  const effects: CareerEffect[] = []
  // M10 P5a6a: the unpacked contents and the pod are weighed by the flown hull's class multipliers (1× today).
  const defId = career.ship.defId
  if (c.contents !== undefined && c.sealed !== true && holdUsedT(c.contents, defId) <= free + EPSILON) {
    let next = career
    for (const w of WARE_IDS) {
      const t = c.contents.wares[w]
      if (t > 0) next = withWare(next, w, t)
    }
    // SY-2b: the goods a death pod carries go back into the hold's goods block.
    const goods = c.contents.goods
    if (goods !== undefined) {
      for (const g of GOOD_IDS) {
        const q = goods[g] ?? 0
        if (q > 0) next = withGood(next, g, q)
      }
    }
    for (const inner of c.contents.components) {
      next = withComponent(next, inner)
      next = logDataCore(next, inner, effects, NO_ORIGIN)
    }
    return ok(next, effects)
  }
  const podT = c.massT / holdMultOf(defId, 'container')
  if (podT > free + EPSILON) return cargoFull(podT, free)
  let next = withComponent(career, c)
  next = logDataCore(next, c, effects, origin)
  return ok(next, effects)
}

/** Removes the component with `id` from the hold (`notInCargo` when it is not there). */
export function removeComponent(career: Career, id: string): CmdResult<Career> {
  const next = withoutComponent(career, id)
  if (next === career) return fail('notInCargo', 'That component is not in the hold.', { id })
  return ok(next)
}

/**
 * Base-price value of pod contents (wares at base price, goods at the strategic base price (SY-2b), components at
 * their value, nested pods included).
 */
export function contentsValue(contents: CargoContents, data: Pick<M2Data, 'wares'>): number {
  let v = goodsBaseValue(contents.goods)
  for (const w of data.wares.wares) v += contents.wares[w.id] * w.basePrice
  for (const c of contents.components) v += componentValue(c, data)
  return v
}

function componentValue(c: CargoComponent, data: Pick<M2Data, 'wares'>): number {
  return c.value + (c.contents !== undefined ? contentsValue(c.contents, data) : 0)
}

/**
 * A base-price estimate of the hold (the encounter director's cargo value): wares at their base price, goods at
 * the strategic base price (SY-2b; the raider curve is retuned for them by SY-2d) plus each component's value
 * (pods add their contents). Station multipliers, drift and saturation are ignored.
 */
export function cargoValue(career: Career, data: Pick<M2Data, 'wares'>): number {
  return contentsValue(career.cargo, data) + goodsBaseValue(career.cargo.parts)
}
